
import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  consigneeList,
  insertConsignee,
  updateConsignee,
} from "../../../services/consigneeService";

const ConsigneeEntry = () => {
  const emptyForm = {
    ShopCode: "",
    Consignee: "",
    ConsigneeName: "",
  };

  const [formData, setFormData] =
    useState(emptyForm);

  const [search, setSearch] =
    useState("");

  const [rows, setRows] =
    useState([]);

  // false = Save mode
  // true = Update mode
  const [editMode, setEditMode] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  // =========================================================
  // LOAD CONSIGNEE LIST
  // =========================================================

  const rptConsigneeList = async () => {
    try {
      setLoading(true);

      const res = await consigneeList();

      setRows(res.data?.data || []);
    } catch (err) {
      console.error(
        "Consignee list error:",
        err
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    rptConsigneeList();
  }, []);

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // CLEAR FORM
  // =========================================================

  const clearForm = () => {
    setFormData(emptyForm);
    setEditMode(false);
  };

  // =========================================================
  // SAVE / INSERT
  // =========================================================

  const handleSave = async () => {
    if (!formData.ShopCode.trim()) {
      alert("Shop Code is required");
      return;
    }

    if (!formData.Consignee.trim()) {
      alert("Consignee Code is required");
      return;
    }

    try {
      setLoading(true);

      const res = await insertConsignee({
        ShopCode:
          formData.ShopCode.trim(),

        Consignee:
          formData.Consignee.trim(),

        ConsigneeName:
          formData.ConsigneeName.trim(),
      });

      if (res.data.success) {
        alert(
          "Consignee saved successfully"
        );

        clearForm();

        await rptConsigneeList();
      } else {
        alert(
          res.data.message ||
            "Unable to save consignee"
        );
      }
    } catch (err) {
      console.error(
        "Insert consignee error:",
        err
      );

      alert(
        err.response?.data?.message ||
          "Error while saving consignee"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // UPDATE
  // =========================================================

  const handleUpdate = async () => {
    if (!formData.ShopCode.trim()) {
      alert("Shop Code is required");
      return;
    }

    if (!formData.Consignee.trim()) {
      alert("Consignee Code is required");
      return;
    }

    try {
      setLoading(true);

      const res = await updateConsignee({
        ShopCode:
          formData.ShopCode.trim(),

        Consignee:
          formData.Consignee.trim(),

        ConsigneeName:
          formData.ConsigneeName.trim(),
      });

      if (res.data.success) {
        alert(
          "Consignee updated successfully"
        );

        clearForm();

        await rptConsigneeList();
      } else {
        alert(
          res.data.message ||
            "Unable to update consignee"
        );
      }
    } catch (err) {
      console.error(
        "Update consignee error:",
        err
      );

      alert(
        err.response?.data?.message ||
          "Error while updating consignee"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DOUBLE CLICK ROW
  // =========================================================

  const handleRowDoubleClick = (row) => {
    setFormData({
      ShopCode: row.ShopCode || "",
      Consignee: row.Consignee || "",
      ConsigneeName:
        row.ConsigneeName || "",
    });

    setEditMode(true);
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const filteredRows = useMemo(() => {
    const value =
      search.toLowerCase().trim();

    if (!value) return rows;

    return rows.filter((row) =>
      [
        row.ShopCode,
        row.Consignee,
        row.ConsigneeName,
      ].some((item) =>
        String(item || "")
          .toLowerCase()
          .includes(value)
      )
    );
  }, [rows, search]);

  // =========================================================
  // UI
  // =========================================================

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        p: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
        boxSizing: "border-box",
      }}
    >
      {/* =====================================================
          PROFESSIONAL HEADER
      ===================================================== */}

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mb: 3,
          p: {
            xs: 2.5,
            md: 3.5,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background */}

        <Box
          sx={{
            position: "absolute",
            right: -50,
            top: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          }}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2.5}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}

          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "white",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <PersonAddIcon
              sx={{
                fontSize: 34,
              }}
            />
          </Box>

          {/* Title */}

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              Consignee Entry
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 700,
              }}
            >
              Add new consignee information or update
              existing consignee records.
            </Typography>
          </Box>

          {/* Status */}

          <Box
            sx={{
              minWidth: {
                xs: "100%",
                sm: 180,
              },
              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Entry Status
            </Typography>

            <Typography
              variant="h6"
              color={
                editMode
                  ? "warning.main"
                  : "primary"
              }
              fontWeight={700}
              sx={{
                mt: 0.3,
              }}
            >
              {editMode
                ? "Editing Record"
                : "New Entry"}
            </Typography>

            <Chip
              label={
                editMode
                  ? "Update Mode"
                  : "Save Mode"
              }
              size="small"
              color={
                editMode
                  ? "warning"
                  : "primary"
              }
              variant="outlined"
              sx={{
                mt: 0.5,
                fontWeight: 600,
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* =====================================================
          ENTRY FORM
      ===================================================== */}

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Form Header */}

        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            {editMode ? (
              <EditIcon
                color="warning"
                sx={{
                  fontSize: 27,
                }}
              />
            ) : (
              <PersonAddIcon
                color="primary"
                sx={{
                  fontSize: 27,
                }}
              />
            )}

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                {editMode
                  ? "Update Consignee"
                  : "Add Consignee"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {editMode
                  ? "Modify the selected consignee details and save the changes."
                  : "Enter the consignee details to create a new record."}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Form Body */}

        <CardContent
          sx={{
            p: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
          }}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            {/* SHOP */}

            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 3,
              }}
            >
              <TextField
                fullWidth
                label="Shop Code"
                name="ShopCode"
                value={formData.ShopCode}
                onChange={handleChange}
                required
                size="small"
                disabled={editMode}
                helperText={
                  editMode
                    ? "Shop code cannot be changed"
                    : "Required"
                }
              />
            </Grid>

            {/* CONSIGNEE */}

            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 3,
              }}
            >
              <TextField
                fullWidth
                label="Consignee Code"
                name="Consignee"
                value={formData.Consignee}
                onChange={handleChange}
                required
                size="small"
                disabled={editMode}
                helperText={
                  editMode
                    ? "Consignee code cannot be changed"
                    : "Required"
                }
              />
            </Grid>

            {/* NAME */}

            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 4,
              }}
            >
              <TextField
                fullWidth
                label="Consignee Name"
                name="ConsigneeName"
                value={formData.ConsigneeName}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            {/* ACTIONS */}

            <Grid
              size={{
                xs: 12,
                md: 2,
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                  md: "column",
                }}
                spacing={1}
              >
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  startIcon={
                    editMode ? (
                      <SaveIcon />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={loading}
                  onClick={
                    editMode
                      ? handleUpdate
                      : handleSave
                  }
                  color={
                    editMode
                      ? "warning"
                      : "primary"
                  }
                  sx={{
                    minHeight: 40,
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  {loading
                    ? "Please wait..."
                    : editMode
                      ? "Update"
                      : "Save"}
                </Button>

                {editMode && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    startIcon={
                      <CancelIcon />
                    }
                    onClick={clearForm}
                    disabled={loading}
                    sx={{
                      minHeight: 40,
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      {/* =====================================================
          LIST / SEARCH
      ===================================================== */}

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* List Header */}

        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <PeopleIcon
                color="primary"
                sx={{
                  fontSize: 28,
                }}
              />

              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                >
                  Consignee Records
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Double-click a record to edit
                  its details.
                </Typography>
              </Box>
            </Stack>

            <Chip
              icon={<PeopleIcon />}
              label={`${filteredRows.length.toLocaleString()} Records`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
              }}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Search */}

        <Box
          sx={{
            p: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Search by shop code, consignee code
              or consignee name.
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
            >
              <TextField
                size="small"
                placeholder="Search consignee..."
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                sx={{
                  width: {
                    xs: "100%",
                    sm: 300,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        color="action"
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="outlined"
                onClick={rptConsigneeList}
                disabled={loading}
                sx={{
                  minWidth: 46,
                  px: 1.5,
                  borderRadius: 2,
                }}
                title="Refresh"
              >
                <RefreshIcon />
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Table */}

        <TableContainer
          sx={{
            width: "100%",
            maxHeight: {
              xs: "60vh",
              sm: 520,
            },
            overflow: "auto",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: {
                xs: 650,
                sm: 750,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "#1976d2",
                    color: "#ffffff",
                    width: "20%",
                  }}
                >
                  Shop Code
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "#1976d2",
                    color: "#ffffff",
                    width: "25%",
                  }}
                >
                  Consignee Code
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "#1976d2",
                    color: "#ffffff",
                    width: "55%",
                  }}
                >
                  Consignee Name
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length > 0 ? (
                filteredRows.map(
                  (row, index) => (
                    <TableRow
                      key={`${row.ShopCode}-${row.Consignee}-${index}`}
                      hover
                      onDoubleClick={() =>
                        handleRowDoubleClick(
                          row
                        )
                      }
                      sx={{
                        cursor: "pointer",

                        "&:nth-of-type(even)": {
                          backgroundColor:
                            "#fafafa",
                        },

                        "&:hover": {
                          backgroundColor:
                            "rgba(25,118,210,0.06)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {row.ShopCode}
                      </TableCell>

                      <TableCell>
                        {row.Consignee}
                      </TableCell>

                      <TableCell>
                        {row.ConsigneeName}
                      </TableCell>
                    </TableRow>
                  )
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{
                      py: 5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      {loading
                        ? "Loading consignee records..."
                        : search
                          ? "No matching records found"
                          : "No consignee records available"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}

        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "grey.50",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Total Records
            </Typography>

            <Typography
              variant="body2"
              fontWeight={700}
              color="primary"
            >
              {filteredRows.length.toLocaleString()}
            </Typography>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ConsigneeEntry;

